import { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import useAuthReady from "../hooks/useAuthReady";
import {
  collection,
  query,
  orderBy,
  getDocs,
} from "firebase/firestore";

export default function SendNotification() {
  /* ===== FORM STATE ===== */
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [postId, setPostId] = useState("");
  const [loading, setLoading] = useState(false);

  /* ===== HISTORY STATE ===== */
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const { ready, user } = useAuthReady();

  /* ================= SEND NOTIFICATION ================= */
  const sendNotification = async () => {
    if (!ready) return alert("Auth still loading");
    if (!user) return alert("Not logged in");
    if (!title.trim() || !body.trim())
      return alert("Title & message required");

    try {
      setLoading(true);

      const token = await auth.currentUser.getIdToken();

      const res = await fetch(
        "https://sendpushnotification-i23bjezngq-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: title.trim(),
            body: body.trim(),
            postId: postId || null,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          typeof data?.error === "string"
            ? data.error
            : "Failed to send notification"
        );
      }

      alert("Notification sent ✅");

      setTitle("");
      setBody("");
      setPostId("");

      // Refresh history after send
      loadHistory();
    } catch (err) {
      console.error("SEND ERROR:", err);
      alert(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* ================= LOAD HISTORY (SAFE) ================= */
  const loadHistory = async () => {
    try {
      setHistoryLoading(true);

      const q = query(
        collection(db, "notifications"),
        orderBy("createdAt", "desc")
      );

      const snap = await getDocs(q);

      const list = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          title: data.title || "",
          body: data.body || "",
          createdAt: data.createdAt,
          sentBy: data.sentBy || {},
        };
      });

      setHistory(list);
    } catch (err) {
      console.error("HISTORY LOAD ERROR:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  return (
    <div className="max-w-3xl space-y-10">

      {/* ===== SEND FORM ===== */}
      <div className="bg-neutral-900 rounded-xl p-6">
        <h2 className="text-lg font-semibold mb-4">
          Send Push Notification
        </h2>

        <input
          placeholder="Notification title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full mb-3 px-4 py-3 rounded-lg
            bg-neutral-950 border border-neutral-800 text-sm"
        />

        <textarea
          rows={4}
          placeholder="Notification message"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full mb-3 px-4 py-3 rounded-lg
            bg-neutral-950 border border-neutral-800 text-sm"
        />

        <input
          placeholder="Post ID (optional)"
          value={postId}
          onChange={(e) => setPostId(e.target.value)}
          className="w-full mb-4 px-4 py-3 rounded-lg
            bg-neutral-950 border border-neutral-800 text-sm"
        />

        <button
          onClick={sendNotification}
          disabled={!ready || loading}
          className="px-5 py-2 bg-red-500 text-black
            font-semibold rounded-lg disabled:opacity-50"
        >
          {loading ? "Sending..." : "Send Notification"}
        </button>
      </div>

      {/* ===== HISTORY ===== */}
      <div className="bg-neutral-900 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-neutral-300 mb-4">
          Notification History
        </h3>

        {historyLoading ? (
          <p className="text-neutral-500 text-sm">
            Loading notifications...
          </p>
        ) : history.length === 0 ? (
          <p className="text-neutral-500 text-sm">
            No notifications sent yet
          </p>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
            {history.map((n) => (
              <div
                key={n.id}
                className="bg-neutral-950 border border-neutral-800
                  rounded-lg p-4"
              >
                <div className="flex justify-between gap-4">
                  <div>
                    <p className="font-medium text-sm">
                      {n.title}
                    </p>
                    <p className="text-sm text-neutral-400 truncate max-w-xl">
                      {n.body}
                    </p>
                  </div>

                  <span className="text-xs text-neutral-500 whitespace-nowrap">
                    {n.createdAt?.toDate
                      ? n.createdAt.toDate().toLocaleString()
                      : "—"}
                  </span>
                </div>

                <p className="text-xs text-neutral-500 mt-2">
                  Sent by: {n.sentBy?.email || "—"}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
